import { API_BASE_URL, DEFAULT_HEADERS } from './config';
import { dispatchSessionExpired, refreshSessionWithQueue } from './sessionRefresh';

export const CSRF_COOKIE_NAME = 'agrosaf_csrf';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

let csrfTokenMemory = null;

export const setCsrfToken = (token) => {
  csrfTokenMemory = token || null;
};

export const clearCsrfToken = () => {
  csrfTokenMemory = null;
};

export const getCsrfToken = () => {
  if (csrfTokenMemory) {
    return csrfTokenMemory;
  }

  if (typeof document !== 'undefined') {
    const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]*)`));
    if (match) {
      const cookieToken = decodeURIComponent(match[1]);
      csrfTokenMemory = cookieToken;
      return cookieToken;
    }
  }

  return csrfTokenMemory;
};

export const getCsrfHeaders = () => {
  const csrfToken = getCsrfToken();
  return csrfToken ? { 'X-CSRF-Token': csrfToken } : {};
};

export const withAuthCredentials = (init = {}) => {
  const csrfHeaders = getCsrfHeaders();
  const existingHeaders = init.headers || {};
  let cleanedHeaders = {};

  if (typeof Headers !== 'undefined' && existingHeaders instanceof Headers) {
    cleanedHeaders = new Headers(existingHeaders);
    Object.entries(csrfHeaders).forEach(([k, v]) => cleanedHeaders.set(k, v));
  } else {
    cleanedHeaders = { ...existingHeaders };
    Object.keys(cleanedHeaders).forEach((key) => {
      if (key.toLowerCase() === 'x-csrf-token') {
        delete cleanedHeaders[key];
      }
    });
    Object.assign(cleanedHeaders, csrfHeaders);
  }

  return {
    ...init,
    credentials: 'include',
    headers: cleanedHeaders
  };
};

export const applyCsrfFromResponse = (responseData) => {
  if (responseData?.csrfToken) {
    setCsrfToken(responseData.csrfToken);
  }
};

const isCsrfErrorPayload = (data) =>
  data?.responseMessage === 'Invalid CSRF token' ||
  data?.message === 'Invalid CSRF token';

export const recoverCsrfToken = async () => {
  const fetchMe = async () => {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      credentials: 'include',
      headers: { ...DEFAULT_HEADERS },
    });

    if (response.status === 401) {
      try {
        await refreshSessionWithQueue();
      } catch {
        return null;
      }

      return fetch(`${API_BASE_URL}/auth/me`, {
        credentials: 'include',
        headers: { ...DEFAULT_HEADERS },
      });
    }

    return response;
  };

  try {
    const response = await fetchMe();

    if (!response || !response.ok) {
      return false;
    }

    const data = await response.json();
    if (data?.data?.csrfToken) {
      applyCsrfFromResponse(data.data);
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

const isCsrfForbiddenResponse = async (response) => {
  if (response.status !== 403) {
    return false;
  }

  try {
    const data = await response.clone().json();
    return isCsrfErrorPayload(data);
  } catch {
    return false;
  }
};

export const authFetch = async (url, init = {}, retryFlags = { csrfRetry: false, authRetry: false }) => {
  const urlStr = typeof url === 'string' ? url : url.toString();
  const isPublicRoute = urlStr.includes('/public/') || urlStr.includes('/jobs/public');
  const method = (init.method || 'GET').toUpperCase();
  const isMutating = !SAFE_METHODS.has(method);

  if (!isPublicRoute && isMutating && !getCsrfToken() && !retryFlags.csrfRetry && !retryFlags.authRetry) {
    await recoverCsrfToken();
  }

  let response = await fetch(url, isPublicRoute ? init : withAuthCredentials(init));

  if (isPublicRoute && (response.status === 401 || response.status === 403)) {
    const cleanInit = { ...init };
    delete cleanInit.credentials;
    response = await fetch(url, cleanInit);
  }

  if (!isPublicRoute && !retryFlags.csrfRetry && isMutating && await isCsrfForbiddenResponse(response)) {
    const recovered = await recoverCsrfToken();
    if (recovered) {
      return authFetch(url, init, { ...retryFlags, csrfRetry: true });
    }
  }

  if (!isPublicRoute && !retryFlags.authRetry && response.status === 401) {
    const isAuthRoute = urlStr.includes('/auth/login') ||
      urlStr.includes('/auth/refresh') ||
      urlStr.includes('/auth/verify-') ||
      urlStr.includes('/auth/forgot-password') ||
      urlStr.includes('/auth/reset-password');

    if (!isAuthRoute) {
      try {
        await refreshSessionWithQueue();
        return authFetch(url, init, { ...retryFlags, authRetry: true });
      } catch {
        dispatchSessionExpired();
      }
    }
  }

  return response;
};
